import {
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { Async, Empty, PageHead, Segmented, Status, useAction, useConfirm } from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Submission } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync } from "@/lib/hooks";

export function SubmissionsPage() {
	const t = useT();
	const { locale } = useI18n();
	const [status, setStatus] = useState<"pending" | "approved" | "rejected" | "duplicate" | "all">("pending");
	const state = useAsync(() => api.submissions({ status, limit: 100 }), [status]);
	const confirm = useConfirm();
	const { run, busy } = useAction();
	const [notes, setNotes] = useState<Record<string, string>>({});

	async function decide(s: Submission, decision: "approved" | "rejected" | "duplicate") {
		if (decision === "approved" && s.kind === "listing") {
			const ok = await confirm(t("submissions.approve"), `${s.payload.name} · ${s.payload.website}`);
			if (!ok) return;
		}
		if (decision !== "approved") {
			const rejecting = decision === "rejected";
			const ok = await confirm(
				t(rejecting ? "submissions.reject" : "submissions.duplicate"),
				t(rejecting ? "submissions.rejectConfirm" : "submissions.duplicateConfirm", {
					name: s.payload.name ?? s.entity_name ?? s.payload.website ?? s.id,
				}),
				true,
			);
			if (!ok) return;
		}
		const r = await run(
			() => api.decide(s.id, decision, notes[s.id] || undefined),
			(r) =>
				r.entity_id
					? t("submissions.approved", { id: r.entity_id })
					: t("submissions.decided", { status: t(`submissions.status.${decision}`) }),
		);
		if (r) state.reload();
	}

	return (
		<>
			<PageHead title={t("submissions.title")} lede={t("submissions.lede")}>
				<Segmented
					value={status}
					onChange={setStatus}
					options={[
						{ value: "pending", label: t("submissions.status.pending") },
						{ value: "approved", label: t("submissions.status.approved") },
						{ value: "rejected", label: t("submissions.status.rejected") },
						{ value: "duplicate", label: t("submissions.status.duplicate") },
						{ value: "all", label: t("state.all") },
					]}
				/>
			</PageHead>
			<Async state={state}>
				{({ submissions }) =>
					submissions.length === 0 ? (
						<Empty />
					) : (
						<div className="table-scroll">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t("submissions.kind")}</TableHead>
										<TableHead>{t("entities.name")}</TableHead>
										<TableHead>{t("submissions.from")}</TableHead>
										<TableHead>{t("state.status")}</TableHead>
										<TableHead>{t("state.created")}</TableHead>
										<TableHead>{t("state.actions")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{submissions.map((s) => (
										<TableRow key={s.id}>
											<TableCell>
												<Status value={s.kind} prefix="submissions.kind" />
											</TableCell>
											<TableCell>
												<strong>{s.payload.name}</strong>
												<span className="sub">
													<a href={s.payload.website} target="_blank" rel="noreferrer">
														{s.payload.website}
													</a>
												</span>
												{s.payload.tagline ? <span className="sub">{s.payload.tagline}</span> : null}
												{s.payload.category ? (
													<span className="sub">
														{t("submissions.category")}: {s.payload.category}
													</span>
												) : null}
												{s.entity_id ? (
													<span className="sub">
														{t("submissions.existing")}:{" "}
														<Link to={`/entities/${s.entity_id}`}>{s.entity_name ?? s.entity_id}</Link>
													</span>
												) : null}
											</TableCell>
											<TableCell>{s.submitter_email ?? "—"}</TableCell>
											<TableCell>
												<Status value={s.status} prefix="submissions.status" />
												{s.note ? <span className="sub">{s.note}</span> : null}
											</TableCell>
											<TableCell>{fmt.dateTime(locale, s.created_at)}</TableCell>
											<TableCell>
												{s.status === "pending" ? (
													<div className="stack" style={{ gap: "var(--s-3)" }}>
														<Input
															placeholder={t("state.note")}
															value={notes[s.id] ?? ""}
															onChange={(e) => setNotes((n) => ({ ...n, [s.id]: e.target.value }))}
														/>
														<div className="row">
															<Button
																variant="primary"
																type="button"
																disabled={busy}
																onClick={() => decide(s, "approved")}
															>
																{t("submissions.approve")}
															</Button>
															<DropdownMenu>
																<DropdownMenuTrigger asChild>
																	<Button
																		variant="default"
																		size="icon"
																		type="button"
																		disabled={busy}
																		aria-label={t("state.moreActions")}
																	>
																		<MoreHorizontal />
																	</Button>
																</DropdownMenuTrigger>
																<DropdownMenuContent align="end">
																	<DropdownMenuItem onSelect={() => decide(s, "duplicate")}>
																		{t("submissions.duplicate")}
																	</DropdownMenuItem>
																	<DropdownMenuItem
																		className="text-[var(--text-error)]"
																		onSelect={() => decide(s, "rejected")}
																	>
																		{t("submissions.reject")}
																	</DropdownMenuItem>
																</DropdownMenuContent>
															</DropdownMenu>
														</div>
													</div>
												) : (
													<span className="faint">{fmt.date(locale, s.reviewed_at)}</span>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)
				}
			</Async>
		</>
	);
}
